import { ImageScaleView, MovieClip, View } from '@play-co/timestep-core/ui';
import { Container, Texture, randomRange } from '../../../odie';
import { getMatch2AssetPath } from '../../util/assets';
import { blockPositionView } from '../../util/blockTools';
import { SharpenerBlockProps } from './SharpenerBlock';
import { CountBlockOptionDef } from '../../defs/block';
import { playAnimationSequence } from '../../util/animation';
import { config } from '../../defs/config';
import gsap from 'gsap';

const manifest = () => ({
  movieclip: {
    url: getMatch2AssetPath('blocks/sharpener'),
    loadMethod: MovieClip.animationLoader,
  },
});

export class SharpenerBlockView extends Container {
  private container: View;
  private sharpener: MovieClip;
  private pencil: ImageScaleView;
  private _count = 0;
  private flipped = false;
  private angle = 0;

  constructor(props: SharpenerBlockProps) {
    super();
    blockPositionView(this, props);

    this.flipped = props.orientation === 'rightToLeft';

    switch (props.orientation) {
      case 'leftToRight':
        this.angle = 0;
        break;
      case 'rightToLeft':
        this.angle = 0;
        break;
      case 'topToBottom':
        this.angle = Math.PI / 2;
        break;
      case 'bottomToTop':
        this.angle = -Math.PI / 2;
        break;
    }

    this.container = new View({
      superview: this.view,
      scaleX: this.flipped ? -1 : 1,
      r: this.angle,
    });

    this.sharpener = new MovieClip({
      superview: this.container,
      url: manifest().movieclip.url,
      defaultAnimation: 'sharpener',
      scale: 0.9,
    });

    this.pencil = new ImageScaleView({
      superview: this.container,
      image: getMatch2AssetPath('blocks/sharpener/26.png'),
      width: 501,
      height: 75,
      scale: 0.9,
      x: 18,
      y: -33,
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 30, right: 30 },
      },
    });

    this.y += 1;
  }

  public static assets() {
    return Object.values(manifest());
  }

  public get count(): number {
    return this._count;
  }

  public set count(value: number) {
    const animated = value < this._count;
    this._count = value;
    this.width = this.count * config.tile.size;
    this.height = config.tile.size;
    this.updateView(animated);
  }

  public async updateView(animated: boolean) {
    const pencilScale = this.pencil.style.scale;
    const pencilWidth = this.count * config.tile.size + 20;
    const width = pencilWidth / pencilScale;

    gsap.killTweensOf(this.pencil.style);

    // If the block is flipped, we need to adjust the x position
    if (this.flipped) {
      this.container.style.x = this.count * config.tile.size;
    }

    // If block is oriented bottomToTop, we need to adjust the y position
    if (this.angle < 0) {
      this.container.style.y = this.count * config.tile.size;
    }

    if (animated) {
      gsap.to(this.pencil.style, {
        width,
        duration: 0.4,
        ease: 'sine.out',
        onUpdate: () => {
          this.pencil.updateOpts({
            r: randomRange(-0.03, 0.03),
            offsetX: randomRange(-2, 2),
            offsetY: randomRange(-2, 2),
          });
        },
        onComplete: () => {
          this.pencil.updateOpts({
            r: 0,
            offsetX: 0,
            offsetY: 0,
          });
        },
      });
    } else {
      this.pencil.updateOpts({ width });
    }

    if (animated) {
      await playAnimationSequence({
        movieclip: this.sharpener,
        animations: ['sharpener_activate', 'sharpener'],
      });
    }
  }

  public async destroyAnimation() {
    await playAnimationSequence({
      movieclip: this.sharpener,
      animations: ['sharpener_die'],
    });
  }
}

export function _createSharpenerBlockView(
  props: SharpenerBlockProps,
  option: number,
): SharpenerBlockView {
  const optionDef = props.options![option] as CountBlockOptionDef;
  const view = new SharpenerBlockView(props);
  view.count = optionDef.count;
  return view;
}
